/** JSON calls to /api. Errors carry the server's code, plain-language message and any extra fields (`details`, such as the field a 400 names). */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details: Record<string, unknown> = {}) { super(message); }
}

/**
 * When the person last typed, clicked or tapped. A shared session's idle limit counts from the last
 * use (sign-in-methods.md B.2), so requests made just after an input say so with X-Vintner-Activity;
 * background refreshes (polling, refetch on focus) don't, and don't keep the session alive.
 */
let lastInput = 0;
export const markActivity = () => { lastInput = Date.now(); };
export const lastActivity = () => lastInput;
if (typeof window !== "undefined") {
  for (const ev of ["pointerdown", "keydown", "touchstart"] as const) window.addEventListener(ev, markActivity, { capture: true, passive: true });
}

/** `headers` adds request headers, for example an Idempotency-Key on a create. */
export async function api<T>(path: string, init: { method?: string; body?: unknown; headers?: Record<string, string> } = {}): Promise<T> {
  const method = init.method ?? (init.body === undefined ? "GET" : "POST");
  const active = method !== "GET" || Date.now() - lastInput < 2000;
  const res = await fetch(`/api${path}`, {
    method,
    headers: { ...(init.body === undefined ? {} : { "Content-Type": "application/json" }), ...(active ? { "X-Vintner-Activity": "user" } : {}), ...init.headers },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: "same-origin",
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = (data as { error?: { code?: string; message?: string } & Record<string, unknown> } | null)?.error;
    const { code, message, ...details } = e ?? {};
    throw new ApiError(res.status, code ?? "error", message ?? "Something went wrong. Try again.", details);
  }
  return data as T;
}

/** Where a sign-in stands (ALN-128): a temporary secret to replace, two-step to set up or enter, or signed in. */
export type Stage = "choose_pin" | "choose_password" | "two_step_setup" | "backup_codes" | "two_step" | "active";

export interface SessionInfo {
  stage: Stage;
  user: { id: string; name: string; email: string; username: string; contactEmail: string | null } | null;
  tenant: { id: string; name: string | null } | null;
  /** The primary role (the highest held), for display. */
  role: string | null;
  /** Every role held at this winery, in rank order (ALN-121). */
  roles?: string[];
  platformAdmin: boolean;
  permissions: string[];
  tenants: { id: string; name: string; role: string; roles?: string[]; pinAllowed: boolean }[];
  /** How this session was proven. */
  signInMethod: "password" | "pin" | "link";
  /** How the person signs in. */
  accountSignInMethod: "password" | "pin";
  /** A computer marked as shared, or any PIN sign-in (B.2). */
  shared: boolean;
  idleMinutes?: number;
  twoStep: { enrolled: boolean; backupCodesLeft: number } | null;
  pinMinLength: number;
}

export type Next = "done" | Exclude<Stage, "active">;
